import { type FC, Fragment } from "preact/compat";
import { Transition } from "@headlessui/react";
import { SnackType } from "~/core";
import { useApp } from "~/core/store";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";
import DoneIcon from "@material-design-icons/svg/outlined/done.svg";
import ErrorIcon from "@material-design-icons/svg/outlined/error.svg";
import InfoIcon from "@material-design-icons/svg/outlined/info.svg";
import WarningIcon from "@material-design-icons/svg/outlined/warning.svg";
import cn from "classnames";

const TYPE_ICONS = {
    success: DoneIcon,
    error: ErrorIcon,
    info: InfoIcon,
    warning: WarningIcon,
};

const getIcon = (type: SnackType) => {
    const Icon = TYPE_ICONS[type];

    return (
        <Icon width="36" height="36" className="min-w-[36px] fill-current" />
    );
};

const Snacker: FC = () => {
    const {
        snack: { items, remove, hold, unhold },
    } = useApp();
    const hasItems = items.value.length > 0;

    return (
        <Transition
            show={hasItems}
            enter="transition-transform transform duration-100"
            enterFrom="translate-y-10 opacity-0"
            enterTo="translate-y-0 opacity-1"
            leave="transition-transform transform duration-100"
            leaveFrom="translate-y-0 opacity-1"
            leaveTo="translate-y-10 opacity-0"
            as={Fragment}
        >
            <div
                className="fixed bottom-[calc(var(--safe-bottom)+0.5rem)] left-6 z-[70] flex flex-col md:left-[56px]"
                onMouseEnter={hold}
                onTouchStart={hold}
                onMouseLeave={unhold}
                onTouchEnd={unhold}
            >
                {items.value.map((snack) => (
                    <div
                        key={snack.id}
                        className={cn(
                            "overflow-y-overlay relative mb-2 rounded-lg border border-gray-300 bg-white p-4 shadow-lg last:mb-0",
                            "w-[calc(100vw-3rem)] max-w-[calc(100vw-3rem)]",
                            "max-h-[120px] min-h-[90px] md:w-[360px] md:max-w-[360px]",
                        )}
                    >
                        <div
                            className={cn(
                                "absolute bottom-0 left-0 top-0 w-2 rounded-l-lg",
                                snack.type === "success" && "bg-green-600",
                                snack.type === "error" && "bg-red-600",
                                snack.type === "warning" && "bg-yellow-600",
                                snack.type === "info" && "bg-gray-600",
                            )}
                        />
                        <button
                            type="button"
                            title="Close message"
                            onClick={() => remove(snack)}
                            className="absolute right-2 top-2 rounded-full fill-current text-gray-500 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-opacity-50"
                        >
                            <CloseIcon width="18" height="18" />
                        </button>
                        {"content" in snack ? (
                            snack.content
                        ) : (
                            <div
                                className={cn(
                                    "flex items-center",
                                    snack.type === "success" &&
                                        "text-green-600",
                                    snack.type === "error" && "text-red-600",
                                    snack.type === "warning" &&
                                        "text-yellow-600",
                                    snack.type === "info" && "text-gray-600",
                                )}
                            >
                                {getIcon(snack.type)}
                                <div className="ml-4">
                                    <h3 className="font-bold">{snack.title}</h3>
                                    <p className="pt-2 text-sm">
                                        {snack.details}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </Transition>
    );
};

export default Snacker;
